/**
 * `clock` zone parameters.
 *
 * Same contract as the programme parsers in ./programme.ts: the API validates
 * and defaults on write (`ClockZoneParamsSchema`), so this is a reader's safety
 * net — an unreadable value falls back to its default rather than blanking a
 * wall. The timezone is carried through verbatim; whether it names a real IANA
 * zone is only decidable by `Intl`, so Clock.vue guards it at render time.
 */

export type ClockFormat = '24h' | '12h';

export interface ClockZoneParams {
  kind: 'clock';
  /** IANA timezone name, e.g. "Europe/Vienna". Guarded at render time. */
  timezone: string;
  format: ClockFormat;
  showDate: boolean;
}

const CLOCK_DEFAULTS = {
  format: '24h',
  showDate: false,
} as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Normalises a zone's stored `parameters` JSON into clock zone parameters, or
 * `null` when the zone is not a clock zone.
 */
export function parseClockZoneParams(parameters: unknown): ClockZoneParams | null {
  if (!isRecord(parameters)) return null;
  if (parameters.kind !== 'clock') return null;
  return {
    kind: 'clock',
    timezone: typeof parameters.timezone === 'string' ? parameters.timezone : '',
    format:
      parameters.format === '12h' || parameters.format === '24h'
        ? parameters.format
        : CLOCK_DEFAULTS.format,
    showDate:
      typeof parameters.showDate === 'boolean' ? parameters.showDate : CLOCK_DEFAULTS.showDate,
  };
}
